import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Tooltip } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { useNavigate, useParams } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { Spin } from 'common/components';
import { removeStorageItem, saveStorageItem } from 'common/helpers/axios.helper';
import { showMessage, showSuccessMessage } from 'common/helpers';
import { EStorageKey } from 'common/const';
import { ArrowLeftLong, Bin, Plus } from 'common/svg';
import { LIST_LIMIT_20 } from 'common/config';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ActiveBasketFooter, BasketGoodsList, BasketOrderDetails } from 'entities/Basket/components';
import {
  filterBasketGoods,
  mapBasketGoodsToPayload,
  mapGoodToBasketGood,
  updateBasketGoodCount,
} from 'entities/Basket/Basket.helper';
import { IBasket, IDeleteBasketGoodPayload, IBasketGoods, IBasketStoresPayload } from 'entities/Basket/Basket.models';
import { IGood } from 'entities/Goods/Goods.models';
import {
  getBasketById,
  getBasketDelivery,
  getBasketStores,
  selectBasket,
  selectIsBasketLoading,
  setCurrentBasket,
  updateBasket,
} from 'entities/Basket/Basket.slice';
import { selectXBasketId, setUserCatalogId, setXBasketId } from 'entities/Auth/Auth.slice';
import { getGoodList, selectIsGoodLoading } from 'entities/Goods/Good.slice';
import { selectCurrentUser } from 'entities/User/User.slice';
import { SelectGoodCard } from 'entities/Goods/components';

export const ActiveBasketPage: FC = () => {
  const dispatch = useAppDispatch();
  // auth
  const xBasketId = useAppSelector(selectXBasketId);
  // basket
  const basket = useAppSelector(selectBasket);
  const isBasketLoading = useAppSelector(selectIsBasketLoading);
  // good
  const isGoodLoading = useAppSelector(selectIsGoodLoading);
  // user
  const currentUser = useAppSelector(selectCurrentUser);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [fetchDeliveryLoading, setFetchDeliveryLoading] = useState<boolean>(false);
  const [goods, setGoods] = useState<IBasketGoods[]>([]);
  const [selectedGoods, setSelectedGoods] = useState<IBasketGoods[]>([]);
  const [isUIUpdateLoading, setIsUIUpdateLoading] = useState<boolean>(false);
  const [isUpdateCountLoading, setIsUpdateCountLoading] = useState<boolean>(false);
  const [isUpdateSelectionLoading, setIsUpdateSelectionLoading] = useState<boolean>(false);
  const [openSelectGoodCard, setOpenSelectGoodCard] = useState<boolean>(false);
  const { id } = useParams();
  const navigate = useNavigate();

  const basketId = id ? Number(id) : undefined;
  const isCurrentBasket = xBasketId === basket?.id;
  const checkAll = goods.length === selectedGoods.length;
  const indeterminate = selectedGoods.length > 0 && selectedGoods.length < goods.length;
  const listLoading = fetchLoading || isGoodLoading || isUpdateCountLoading || isUpdateSelectionLoading;

  const getDeliveryDetails = async (goods: IBasketGoods[], params?: { basket?: IBasket | null }): Promise<void> => {
    const userBasket = params?.basket ?? basket;

    if (!userBasket?.account.isSelfRegistered || !goods.length) {
      return;
    }

    setFetchDeliveryLoading(true);

    if (userBasket?.address?.cityId) {
      const payload: IBasketStoresPayload = {
        cityId: userBasket.address.cityId,
        goods: mapBasketGoodsToPayload(goods),
      };

      await dispatch(getBasketStores(payload));
      await dispatch(getBasketDelivery(payload));
    }

    setFetchDeliveryLoading(false);
  };

  const fetchData = async (): Promise<void> => {
    if (!basketId) {
      setFetchLoading(false);
      return;
    }

    const response = await dispatch(getBasketById(basketId));

    if (!response) {
      setFetchLoading(false);
      return;
    }

    const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(response.goods, response.maxGoodPrice);

    await getDeliveryDetails(availableGoods, { basket: response });

    setGoods(response.goods);
    setSelectedGoods(availableGoods);
    setFetchLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [basketId]);

  const debouncedUpdateBasketGoods = useMemo(
    () =>
      debounce(async (goods: IBasketGoods[], selectedGoods: IBasketGoods[]) => {
        if (!basket) {
          setIsUIUpdateLoading(false);
          return;
        }

        setIsUpdateCountLoading(true);

        try {
          const response = await dispatch(updateBasket({ id: basket.id, goods: mapBasketGoodsToPayload(goods) }));

          if (response) {
            const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(response.goods, response.maxGoodPrice);
            const availableGoodIds = new Set(availableGoods.map((i) => i.goodId));
            const newSelectedGoods = selectedGoods.filter((item) => availableGoodIds.has(item.goodId));

            await getDeliveryDetails(newSelectedGoods, { basket: response });
          }
        } finally {
          setIsUIUpdateLoading(false);
          setIsUpdateCountLoading(false);
        }
      }, 800),
    [basket],
  );

  useEffect(() => {
    return () => {
      debouncedUpdateBasketGoods.cancel();
    };
  }, [debouncedUpdateBasketGoods]);

  const changeGoodCount = (id: number, count: number): void => {
    const newGoods = updateBasketGoodCount(goods, id, count);
    const newSelectedGoods = updateBasketGoodCount(selectedGoods, id, count);

    setIsUIUpdateLoading(true);
    setGoods(newGoods);
    setSelectedGoods(newSelectedGoods);
    debouncedUpdateBasketGoods(newGoods, newSelectedGoods);
  };

  const changeCheckAll = async (e: CheckboxChangeEvent): Promise<void> => {
    if (indeterminate) {
      setSelectedGoods([]);
      return;
    }

    setIsUpdateSelectionLoading(true);

    if (e.target.checked) {
      const availableGoods = filterBasketGoods.getAvailableForCustomerGoods(goods, basket?.maxGoodPrice);

      setSelectedGoods(availableGoods);
      await getDeliveryDetails(availableGoods);
    } else {
      setSelectedGoods([]);
    }

    setIsUpdateSelectionLoading(false);
  };

  const changeGoodSelection = async (good: IBasketGoods, checked: boolean): Promise<void> => {
    const newSelectedGoods = checked ? [...selectedGoods, good] : filterBasketGoods.notEqualId(selectedGoods, good.goodId);

    setIsUpdateSelectionLoading(true);
    setSelectedGoods(newSelectedGoods);
    await getDeliveryDetails(newSelectedGoods);
    setIsUpdateSelectionLoading(false);
  };

  const deleteGood = ({ id, onSuccess }: IDeleteBasketGoodPayload): void => {
    if (basket && id) {
      const filteredGoods = filterBasketGoods.notEqualId(goods, id);

      dispatch(
        updateBasket({
          id: basket.id,
          goods: mapBasketGoodsToPayload(filteredGoods),
          onSuccess: async () => {
            const filteredSelectedGoods = filterBasketGoods.notEqualId(selectedGoods, id);

            setGoods(filteredGoods);
            setSelectedGoods(filteredSelectedGoods);
            onSuccess?.();
            await getDeliveryDetails(filteredSelectedGoods);
            showMessage({ content: 'Товар удален из корзины.', icon: <Bin className="icon-bin-dark-grey" /> });
          },
        }),
      );
    }
  };

  const handleOpenSelectGoodCard = async (): Promise<void> => {
    await dispatch(
      getGoodList({
        goodInBasket: false,
        sellerId: currentUser?.account.id,
        limit: LIST_LIMIT_20,
        offset: 0,
      }),
    );
    setOpenSelectGoodCard(true);
  };

  const closeSelectGoodCard = (): void => {
    setOpenSelectGoodCard(false);
  };

  const addGoodToBasket = (good: IGood, onSuccess?: () => void): void => {
    if (basket) {
      const basketGood = mapGoodToBasketGood(good);
      const newGoods = [...goods, basketGood];

      dispatch(
        updateBasket({
          id: basket.id,
          goods: mapBasketGoodsToPayload(newGoods),
          onSuccess: async () => {
            const [availableGood] = filterBasketGoods.getAvailableForCustomerGoods([basketGood], basket.maxGoodPrice);
            const newSelectedGoods = availableGood ? [...selectedGoods, availableGood] : selectedGoods;

            setGoods(newGoods);
            setSelectedGoods(newSelectedGoods);
            onSuccess?.();
            await getDeliveryDetails(newSelectedGoods);
          },
        }),
      );
    }
  };

  const onLinkBasket = (): void => {
    if (basket) {
      saveStorageItem(EStorageKey.XBasketId, basket.id);
      saveStorageItem(EStorageKey.UserCatalogId, basket.defaultCatalogId as number);
      dispatch(setCurrentBasket(basket));
      dispatch(setXBasketId(basket.id));
      dispatch(setUserCatalogId(basket.defaultCatalogId as number));
      showSuccessMessage(`Вы можете добавлять товары в корзину #${basket.id}.`);
    }
  };

  const onUnlinkBasket = (): void => {
    removeStorageItem(EStorageKey.XBasketId);
    removeStorageItem(EStorageKey.UserCatalogId);
    dispatch(setCurrentBasket(null));
    dispatch(setXBasketId(null));
    dispatch(setUserCatalogId(null));
    showSuccessMessage(`Корзина #${basket?.id} успешно отвязана от аккаунта.`);
  };

  return (
    <>
      <div className="redesign h-full flex">
        <div className="pt-11 px-11 flex direction-column flex-1">
          <div className="mb-14 flex align-center justify-end">
            <div className="w-full">
              <Button className="button-circle secondary" icon={<ArrowLeftLong />} onClick={() => navigate(-1)} />
            </div>

            {isCurrentBasket && (
              <Tooltip overlayClassName="redesign" title="Быстрое добавление товара">
                <Button
                  className="button-circle primary"
                  icon={<Plus className="icon-plus-white" />}
                  onClick={handleOpenSelectGoodCard}
                />
              </Tooltip>
            )}
          </div>

          <div className="relative px-11 flex direction-column flex-1">
            <div className="relative mb-13">
              <span className="ff-regular fs-xxl">Корзина</span>

              <span className="absolute ml-2 ff-regular fs-l" style={{ top: 0 }}>
                {basket?.goods.length ?? 0}
              </span>
            </div>

            {!!goods.length && (
              <div className="mb-5 ml-3">
                <Checkbox checked={checkAll} indeterminate={indeterminate} onChange={changeCheckAll}>
                  Включить в заказ
                </Checkbox>
              </div>
            )}

            <Spin spinning={listLoading}>
              <BasketGoodsList
                basket={basket}
                goods={goods}
                selectedGoods={selectedGoods}
                loading={isBasketLoading}
                onCountChange={changeGoodCount}
                onSelectGood={changeGoodSelection}
                onDeleteGood={deleteGood}
              />
            </Spin>

            <ActiveBasketFooter
              basket={basket}
              isCurrentBasket={isCurrentBasket}
              onLink={onLinkBasket}
              onUnlink={onUnlinkBasket}
            />
          </div>
        </div>

        {!!goods.length && (
          <BasketOrderDetails basket={basket} selectedGoods={selectedGoods} loading={isUIUpdateLoading || fetchDeliveryLoading} />
        )}
      </div>

      <SelectGoodCard
        open={openSelectGoodCard}
        loading={isBasketLoading}
        goodInBasket={false}
        sellerId={currentUser?.account.id}
        onCancel={closeSelectGoodCard}
        onSelect={addGoodToBasket}
      />
    </>
  );
};
